import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthUser } from '../store/auth.state';
import { SignupResponse } from '../models/signup-response.models';
import { SignupRequest } from '../models/signup-request.models';
import { PasswordresetemailRequest } from '../models/passwordresetemail-request.models';
import { PasswordresetemailResponse } from '../models/passwordresetemail-response.models';
import { PasswordresetRequest } from '../models/passwordreset-request.models';
import { PasswordresetResponse } from '../models/passwordreset-response.models';
import { LoginResponse } from '../models/login-response.models';
import { DynamicUiService } from 'src/app/dynamic-ui/services/dynamic-ui.service';
import { AppConstants } from 'src/app/app/helpers/app-constants';

export interface AccessData {
  token_type: 'Bearer';
  expires_in: number;
  access_token: string;
  refresh_token: string;
}

@Injectable()
export class AuthService {

  readonly NameSessionGuid = 'SessionGuid';
  readonly NameSecretGuid = 'SecretGuid';
  
  constructor(
    private http: HttpClient,
    private dynamicUiService: DynamicUiService
  ) {
  }

  setSessionGuid(sessionGuid: string) {
    sessionStorage.setItem(this.NameSessionGuid, sessionGuid);
  }

  getSessionGuid(): string {
    return sessionStorage.getItem(this.NameSessionGuid);
  }

  setSecretGuid(secretGuid: string) {
    sessionStorage.setItem(this.NameSecretGuid, secretGuid);
  }

  getSecretGuid(): string {
    return sessionStorage.getItem(this.NameSecretGuid);
  }

  /**
   * Returns a promise that waits until
   * refresh token and get auth user
   *
   * @returns {Promise<AuthState>}
   */
  // init(): Promise<AuthState> {
  //   this.store.dispatch(AuthActions.refreshToken());

  //   const authState$ = this.store.select(AuthSelectors.authQuery).pipe(
  //     filter(
  //       auth =>
  //         auth.refreshTokenStatus === TokenStatus.INVALID ||
  //         (auth.refreshTokenStatus === TokenStatus.VALID && !!auth.user)
  //     ),
  //     take(1)
  //   );

  //   return lastValueFrom(authState$);
  // }

  /**
   * Performs a request with user credentials
   * in order to get auth tokens
   *
   * @param {string} username
   * @param {string} password
   * @returns Observable<AccessData>
   */
  login(username: string, password: string, rememberThisUser: boolean): Observable<LoginResponse> {
    return this.http.post<LoginResponse>("/api/auth/login", {
      'username': username,
      'password': password,
      'rememberThisUser': rememberThisUser
    });
  }

  /**
   * Performs a request for logout authenticated user
   *
   * @param {('all' | 'allButCurrent' | 'current')} [clients='current']
   * @returns Observable<void>
   */
  logout(): Observable<void> {
    return this.http.get<void>("/api/auth/logout");
  }

  /**
   * Asks for a new access token given
   * the stored refresh token
   *
   * @returns {Observable<AccessData>}
   */
  refreshToken(): Observable<void> {
    return this.http.get<void>("/api/auth/refresh_token");
  }

  /**
   * Returns authenticated user
   * based on saved access token
   *
   * @returns {Observable<AuthUser>}
   */
  getAuthUser(): Observable<AuthUser> {

    let vereinId: string = this.dynamicUiService.GetValue(AppConstants.dataStoreCurrentVereinId, []);
    let anlassId: string = this.dynamicUiService.GetValue(AppConstants.dataStoreCurrentAnlassId, []);

    const headers = { 'content-type': 'application/json' }

    let queryParams = new HttpParams();
    if (vereinId ?? '' !== '') queryParams = queryParams.append("vereinId", vereinId);
    if (anlassId ?? '' !== '') queryParams = queryParams.append("anlassId", anlassId);
    return this.http.get<AuthUser>("/api/auth/userinformation", { 'params': queryParams, 'headers': headers });
  }

  signUp(signUpRequest: SignupRequest): Observable<SignupResponse> {
    return this.http.post<SignupResponse>("/api/auth/signup", signUpRequest);
  }

  passwordResetEmail(passwordResetEmailRequest: PasswordresetemailRequest): Observable<PasswordresetemailResponse> {
    return this.http.post<PasswordresetemailResponse>("/api/auth/passwordresetemail", passwordResetEmailRequest);
  }

  passwordReset(passwordResetRequest: PasswordresetRequest): Observable<PasswordresetResponse> {
    return this.http.post<PasswordresetResponse>("/api/auth/passwordreset", passwordResetRequest);
  }
}

// export const authServiceInitProvider: Provider = {
//   provide: APP_INITIALIZER,
//   useFactory: (authService: AuthService) => () => authService.init(),
//   deps: [AuthService],
//   multi: true,
// };
